import React from 'react';
import { User, Bot, ChevronRight, Sparkles, HelpCircle, DollarSign, Cpu, Calendar } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

interface EnhancedChatMessageProps {
  message: {
    message: string;
    reply?: string;
    timestamp: string;
    agent_type?: string;
    confidence?: number;
    tone?: string;
    suggestedFollowups?: string[];
    sources?: Array<{
      type: string;
      snippet: string;
      relevanceScore?: number;
    }>;
  };
  onFollowupClick?: (question: string) => void;
}

export function EnhancedChatMessage({ message, onFollowupClick }: EnhancedChatMessageProps) {
  // Function to highlight important information in the reply
  const formatReply = (text: string) => {
    if (!text) return null;
    
    // Patterns to highlight
    const patterns = [
      // Prices - match currency symbols and numbers
      { regex: /(\$[\d,]+\.?\d*)/g, className: 'font-bold text-green-600 dark:text-green-400 text-lg' },
      // Percentages
      { regex: /(\d+\.?\d*%)/g, className: 'font-semibold text-blue-600 dark:text-blue-400' },
      // Technical specs (processor, memory, etc.)
      { regex: /(ARM Cortex-[A-Z0-9]+|Quad-Core|Dual-Core|\d+GB?Hz|\d+GB? RAM|\d+GB? storage)/gi, className: 'font-mono font-semibold text-purple-600 dark:text-purple-400' },
      // Ages
      { regex: /(\d+[-–]\d+\s*years?|\d+\+?\s*years?)/g, className: 'font-semibold text-orange-600 dark:text-orange-400' },
      // Product names (assuming they're capitalized)
      { regex: /(CosmoBot\s*\w*|FluffyTown\s*\w*|Mega\s*\w*)/g, className: 'font-semibold text-indigo-600 dark:text-indigo-400' },
      // Dimensions/measurements
      { regex: /(\d+\.?\d*\s*(cm|mm|m|inches?|feet|ft|lbs?|kg|g))/gi, className: 'font-mono text-teal-600 dark:text-teal-400' },
    ];
    
    let formattedText = text;
    const replacements: Array<{ start: number; end: number; replacement: string }> = [];
    
    // Find all matches and their positions
    patterns.forEach(({ regex, className }) => {
      let match;
      while ((match = regex.exec(text)) !== null) {
        replacements.push({
          start: match.index,
          end: match.index + match[0].length,
          replacement: `<span class="${className}">${match[0]}</span>`
        });
      }
    });
    
    // Sort replacements by position (reverse order to maintain indices)
    replacements.sort((a, b) => b.start - a.start);
    
    // Apply replacements
    replacements.forEach(({ start, end, replacement }) => {
      formattedText = formattedText.slice(0, start) + replacement + formattedText.slice(end);
    });
    
    return <div dangerouslySetInnerHTML={{ __html: formattedText }} />;
  };
  
  // Function to get icon for followup question type
  const getFollowupIcon = (question: string) => {
    const lowerQuestion = question.toLowerCase();
    if (lowerQuestion.includes('price') || lowerQuestion.includes('cost')) return DollarSign;
    if (lowerQuestion.includes('technical') || lowerQuestion.includes('processor') || lowerQuestion.includes('memory')) return Cpu;
    if (lowerQuestion.includes('age') || lowerQuestion.includes('suitable')) return Calendar;
    return HelpCircle;
  };
  
  return (
    <div className="space-y-3">
      {/* User Message */}
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">
          <div className="h-8 w-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
            <User className="h-4 w-4 text-gray-600 dark:text-gray-400" />
          </div>
        </div>
        <div className="flex-1 space-y-1">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">You</p>
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg px-4 py-3">
            <p className="text-sm text-gray-800 dark:text-gray-200">{message.message}</p>
          </div>
        </div>
      </div>
      
      {/* Bot Reply */}
      {message.reply && (
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-primary" />
            </div>
          </div>
          <div className="flex-1 space-y-3">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">AI Assistant</p>
              <Badge variant="secondary" className="text-xs">
                {message.agent_type || 'chatbot'}
              </Badge>
              {message.confidence && (
                <Badge 
                  variant="outline" 
                  className={cn(
                    "text-xs",
                    message.confidence > 0.8 ? "text-green-600 border-green-600" : 
                    message.confidence > 0.5 ? "text-yellow-600 border-yellow-600" : 
                    "text-red-600 border-red-600"
                  )}
                >
                  {Math.round(message.confidence * 100)}% confident
                </Badge>
              )}
            </div>
            
            {/* Main Reply with Formatting */}
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-3 shadow-sm">
              <div className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed">
                {formatReply(message.reply)}
              </div>
            </div>
            
            {/* Sources (if available) */}
            {message.sources && message.sources.length > 0 && (
              <div className="text-xs text-gray-500 dark:text-gray-400 italic">
                Based on {message.sources.length} source{message.sources.length > 1 ? 's' : ''}
                {message.sources[0].relevanceScore && 
                  ` (${Math.round((message.sources[0].relevanceScore || 0) * 100)}% relevant)`
                }
              </div>
            )}
            
            {/* Suggested Followups */}
            {message.suggestedFollowups && message.suggestedFollowups.length > 0 && (
              <div className="space-y-2 pt-2">
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                  You might also ask:
                </p>
                <div className="flex flex-wrap gap-2">
                  {message.suggestedFollowups.map((followup, idx) => {
                    const Icon = getFollowupIcon(followup);
                    return (
                      <Button
                        key={idx}
                        variant="outline"
                        size="sm"
                        className="text-xs h-8 px-3 hover:bg-primary/5 hover:border-primary/50 transition-colors group"
                        onClick={() => onFollowupClick?.(followup)}
                      >
                        <Icon className="h-3 w-3 mr-1.5 text-gray-500 group-hover:text-primary" />
                        {followup}
                        <ChevronRight className="h-3 w-3 ml-1.5 text-gray-400 group-hover:text-primary" />
                      </Button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}